/**
 * Conversation routing (pure; the services load rows and apply the result).
 *
 *  1. The first enabled routing rule (by position) whose conditions all match picks a team and optional skill.
 *  2. Otherwise the connected account's default team is used.
 *  3. Within the team (or the whole tenant when there is no team) the agent is chosen by: has the required skill,
 *     speaks the conversation's language, fewest open conversations, then a stable tie-break.
 */
export const LANGUAGES = ['en', 'ar', 'hi', 'ml', 'ta', 'ur', 'tl'] as const;
export const SKILL_RE = /^[a-z0-9][a-z0-9_-]{0,39}$/;

export interface RoutingRule {
  id: string;
  enabled: boolean;
  position: number;
  channel: string | null;
  channelAccountId: string | null;
  language: string | null;
  keywords: string[];
  teamId: string;
  skill: string | null;
}

export interface RoutingInput {
  channel: string;
  channelAccountId: string | null;
  language: string | null;
  text: string;
}

export interface Candidate {
  userId: string;
  skills: string[];
  /** Languages from team membership; empty means "use the user's own locale". */
  languages: string[];
  locale: string;
  openCount: number;
}

/** Lower-cases and strips Arabic diacritics/tatweel and alef/yaa/taa-marbuta variants, so keywords match loosely. */
export function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[إأآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/\s+/g, ' ')
    .trim();
}

export function ruleMatches(rule: RoutingRule, input: RoutingInput): boolean {
  if (!rule.enabled) return false;
  if (rule.channel && rule.channel !== input.channel) return false;
  if (rule.channelAccountId && rule.channelAccountId !== input.channelAccountId) return false;
  if (rule.language && rule.language !== input.language) return false;
  if (rule.keywords.length) {
    const text = normalizeForMatch(input.text);
    if (!rule.keywords.some((k) => k.trim() && text.includes(normalizeForMatch(k)))) return false;
  }
  return true;
}

export function chooseTeam(
  rules: RoutingRule[],
  input: RoutingInput,
  accountDefaultTeamId: string | null,
): { teamId: string | null; skill: string | null; ruleId: string | null } {
  const rule = [...rules].sort((a, b) => a.position - b.position).find((r) => ruleMatches(r, input));
  if (rule) return { teamId: rule.teamId, skill: rule.skill, ruleId: rule.id };
  return { teamId: accountDefaultTeamId, skill: null, ruleId: null };
}

export function chooseAgent(
  candidates: Candidate[],
  need: { skill: string | null; language: string | null },
): string | null {
  const speaks = (c: Candidate) =>
    !!need.language &&
    (c.languages.length ? c.languages.includes(need.language) : c.locale === need.language);
  const ranked = [...candidates].sort(
    (a, b) =>
      Number(!!need.skill && b.skills.includes(need.skill)) -
        Number(!!need.skill && a.skills.includes(need.skill)) ||
      Number(speaks(b)) - Number(speaks(a)) ||
      a.openCount - b.openCount ||
      a.userId.localeCompare(b.userId),
  );
  return ranked[0]?.userId ?? null;
}

/** Parses "VIP, Group bookings" into ["vip", "group-bookings"] (deduplicated, invalid entries dropped). */
export function parseSkills(input: string | string[]): string[] {
  const parts = Array.isArray(input) ? input : input.split(/[,،\n]/);
  const out = parts.map((s) => s.trim().toLowerCase().replace(/\s+/g, '-')).filter((s) => SKILL_RE.test(s));
  return [...new Set(out)];
}
